// src/modules/app-views/fiscal-config/pages/Document/tabs/NfeTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Alert } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import NfeCard from './NfeCard';
import VoidNfeCard from './VoidNfeCard';
import showVoidNfeModal from './modals/VoidNfeModal';
import { useFiscalState } from 'modules/app-views/fiscalConfig/pages/Document/context/FiscalStateContext';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const RESOURCE_NFE = 'moduleresources_nfe';

// Fields required to activate NFe service with their error messages
const NFE_REQUIRED_FIELDS = [
  { path: ['fiscalData', 'nfe', 'provider'], messageKey: 'inform_nfe_provider' },
  { path: ['fiscalData', 'nfe', 'environment'], messageKey: 'inform_nfe_environment' },
  { path: ['fiscalData', 'nfe', 'currentSerie'], messageKey: 'inform_nfe_serie' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfeTab = ({ setState, state = {}, ...restProps }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { data, loading, refresh } = useDocumentContext();
  const { form, locked, goFocus } = useSimpleFormContext();
  const { setServiceEnabled } = useFiscalState();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes(RESOURCE_NFE);
  const entityId = data?.entityId || data?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [nfeEnabled, setNfeEnabled] = useState(data?.fiscalData?.nfe?.isEnabled || false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync NFe enabled state when data changes
  useEffect(() => {
    if (data?.fiscalData?.nfe?.isEnabled !== undefined) {
      setNfeEnabled(data.fiscalData.nfe.isEnabled);
    }
  }, [data?.fiscalData?.nfe?.isEnabled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Silent save helper - saves isEnabled state without changing document mode
  const saveServiceEnabled = useCallback(
    async isEnabled => {
      if (!entityId) return;
      try {
        await axiosService.put({
          url: API.ENTITIES.URL.replace(':_id?', entityId),
          data: { 'fiscalData.nfe.isEnabled': isEnabled },
        });
      } catch (error) {
        console.error('[NfeTab] Silent save error:', error);
      }
    },
    [entityId]
  );

  const handleOpenVoidNfeModal = useCallback(() => {
    showVoidNfeModal({
      company: data,
      onSuccess: ({ protocolNumber }) => {
        refresh?.();
      },
    });
  }, [data, refresh]);

  const handleNfeToggle = useCallback(
    async checked => {
      // Clear previous validation error
      setValidationError(null);

      // If turning OFF, update local state and save silently
      if (!checked) {
        setNfeEnabled(false);
        setServiceEnabled('nfe', false); // Update context for CardHeader tags
        if (form) {
          form.setFieldValue(['fiscalData', 'nfe', 'isEnabled'], false);
        }
        // Silent save to backend and refresh to update card header tags
        // await saveServiceEnabled(false);
        // refresh?.();
        appService.message({ flag: 's', text: 'service_deactivated', duration: 2 });
        return;
      }

      // If turning ON, validate form fields first
      if (!entityId) {
        appService.notification({
          flag: 'error',
          title: 'error',
          description: 'entity_not_found',
        });
        return;
      }

      // Show loading message (will be updated based on results)
      const msg = await appService.message({
        flag: 'loading',
        text: 'validating_nfe_data',
        duration: 0, // Keep visible until we update it
      });

      setValidating(true);

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 1: Validate required fields (MANUAL - fields don't have rules)
      // ─────────────────────────────────────────────────────────────────────────
      // Manual validation - check if required fields have values
      const emptyFields = [];
      NFE_REQUIRED_FIELDS.forEach(({ path, messageKey }) => {
        const value = form?.getFieldValue(path);
        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty) {
          emptyFields.push({ name: path, messageKey });
        }
      });

      if (emptyFields.length > 0) {
        // Get the first empty field's message key for a specific error message
        const firstEmptyField = emptyFields[0];

        msg.update({
          flag: 'warning',
          text: firstEmptyField.messageKey,
          duration: 4,
        });

        // Mark empty fields as error in the form (show red border)
        const fieldsToMark = emptyFields.map(({ name }) => ({
          name,
          errors: [translateX('required')],
        }));
        form.setFields(fieldsToMark);

        // Use goFocus to focus the first empty required field
        if (goFocus && firstEmptyField.name) {
          goFocus(firstEmptyField.name.join('.'));
        }

        setValidating(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 2: Backend validation (form passed)
      // ─────────────────────────────────────────────────────────────────────────
      try {
        // Get form values for API validation
        const formValues = form?.getFieldsValue(true) || {};
        const nfeData = formValues?.fiscalData?.nfe || {};

        const [response] = await axiosService.post({
          url: API.FISCAL.SETUP_VALIDATE_SERVICE,
          data: {
            entityId,
            service: 'nfe',
            provider: nfeData.provider,
            fiscalData: {
              nfe: {
                environment: nfeData.environment,
                currentSerie: nfeData.currentSerie,
              },
            },
          },
          returnAsObject: true,
        });

        if (response?.isValid) {
          // Validation successful - activate service
          setNfeEnabled(true);
          setServiceEnabled('nfe', true); // Update context for CardHeader tags
          if (form) {
            form.setFieldValue(['fiscalData', 'nfe', 'isEnabled'], true);
          }

          // Silent save to backend and refresh to update card header tags
          // await saveServiceEnabled(true);
          // refresh?.();

          msg.update({
            flag: 'success',
            text: 'service_enabled',
            duration: 3,
          });
        } else {
          // Backend validation failed - show error
          const errors = response?.errorList || [];
          const errorMessages = errors.map(e => translateX(e.i18nKey) || e.message).join('\n');

          msg.update({
            flag: 'error',
            text: 'nfe_configuration_not_validated',
            duration: 4,
          });

          // Show Alert with detailed error from backend
          setValidationError(errorMessages || translateX('validation_failed_unknown'));
        }
      } catch (error) {
        console.error('[NfeTab] Validation error:', error);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          translateX('validation_error_occurred');

        msg.update({
          flag: 'error',
          text: 'nfe_configuration_not_validated',
          duration: 4,
        });

        // Show Alert with detailed error
        setValidationError(errorMessage);
      } finally {
        setValidating(false);
      }
    },
    [entityId, form, goFocus, saveServiceEnabled, setServiceEnabled]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // If resource is not contracted, show alert
  if (!hasNfeResource) {
    return (
      <div className="container" style={{ position: 'relative', minHeight: 120 }}>
        <Alert
          type="warning"
          showIcon
          icon={<StopOutlined />}
          title={translateX('module_not_contracted')}
          description={translateX('nfe_module_not_contracted_description')}
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="nfeData" isFetching={loading} data={data} />

      <NfeCard
        nfeEnabled={nfeEnabled}
        locked={locked}
        validating={validating}
        validationError={validationError}
        onToggle={handleNfeToggle}
      />

      <VoidNfeCard onOpenVoidNfeModal={handleOpenVoidNfeModal} />

    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NfeTab;
